/**
 * Browser events that matter for a long-lived connection on a phone. Abstracted so the
 * network client can run in Node (tests) without a DOM.
 */
export interface NetLifecycle {
  /** The page became visible again: tab switched back, phone unlocked, app reopened. */
  onResume(listener: () => void): () => void;
  /** The browser reports that the network is back (Wi-Fi reconnected, plane mode off). */
  onOnline(listener: () => void): () => void;
  /**
   * The device switched networks (Wi-Fi to mobile data and back), where the browser tells:
   * the Network Information API, Chrome on Android. Elsewhere it never fires.
   */
  onNetworkChange(listener: () => void): () => void;
  /** The page is going away for good: tab or window closed, reloaded, navigated elsewhere. */
  onLeave(listener: () => void): () => void;
}

/** Lifecycle that never fires (Node, tests). */
export const NO_LIFECYCLE: NetLifecycle = {
  onResume: () => () => {},
  onOnline: () => () => {},
  onNetworkChange: () => () => {},
  onLeave: () => () => {},
};

export function browserLifecycle(): NetLifecycle {
  return {
    onResume(listener) {
      const onVisibility = () => {
        if (document.visibilityState === 'visible') listener();
      };
      // `pageshow` with persisted = restored from the back/forward cache with dead sockets.
      const onPageShow = (event: PageTransitionEvent) => {
        if (event.persisted) listener();
      };
      document.addEventListener('visibilitychange', onVisibility);
      window.addEventListener('pageshow', onPageShow);
      return () => {
        document.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener('pageshow', onPageShow);
      };
    },
    onOnline(listener) {
      window.addEventListener('online', listener);
      return () => window.removeEventListener('online', listener);
    },
    onNetworkChange(listener) {
      const connection = (navigator as Navigator & { connection?: EventTarget }).connection;
      if (typeof connection?.addEventListener !== 'function') return () => {};
      connection.addEventListener('change', listener);
      return () => connection.removeEventListener('change', listener);
    },
    onLeave(listener) {
      // `pagehide` comes with every unload (closing, reloading, navigating away), also on
      // phones, where `unload` and `beforeunload` often do not. A page kept in the
      // back/forward cache (`persisted`) may come back: that is not leaving.
      const onPageHide = (event: PageTransitionEvent) => {
        if (!event.persisted) listener();
      };
      window.addEventListener('pagehide', onPageHide);
      return () => window.removeEventListener('pagehide', onPageHide);
    },
  };
}
